import { useEffect, useRef, useState } from 'react'
import { DatePicker } from '../ui/DatePicker'
import { checkRange, daysBetween, rangeLabel, today, type BoardRange } from './windowRange'

/**
 * FEREASTRA DE TIMP DIN CAPUL TABLOULUI - cele patru de-a gata, si `Custom`.
 *
 * -----------------------------------------------------------------------------
 * CE ERA SI CE S-A ADAUGAT
 * -----------------------------------------------------------------------------
 *
 * Erau patru legaturi: Today, Last 7 days, Last 30 days, Last 90 days. Raman
 * EXACT cum erau - aceleasi `<a>`, aceleasi adrese, aceeasi clasa `odb-window`.
 * Langa ele vine `Custom`, care deschide doua calendare si trimite `from`/`to`.
 *
 * Ferestrele fixe sunt legaturi fiindca asa erau: se deschid cu clic de mijloc
 * intr-o fila noua, se copiaza cu dreapta. Perioada nu poate fi o legatura -
 * nu se stie unde duce pana nu alege omul - deci e un buton cu un panou.
 *
 * -----------------------------------------------------------------------------
 * PANOUL CHIAR SE VEDE
 * -----------------------------------------------------------------------------
 *
 * Foile vechi ale portalului sting panourile dupa clasa: `app.css` are
 * `.datetimepicker > div { display: none }`, si din cauza ei calendarele
 * ieseau casute albe de 234x2 pixeli pe toate ecranele (livrarea 26.9.18.6).
 * De-aia panoul de aici NU poarta `dropdown-menu` si nicio clasa de-a lor, ci
 * una proprie, cu asezarea scrisa in linie. `DatePicker` isi poarta singur
 * leacul pentru calendarul dinauntru.
 *
 * -----------------------------------------------------------------------------
 * SE APLICA NUMAI CAND AMANDOUA SUNT ALESE SI BUNE
 * -----------------------------------------------------------------------------
 *
 * Cat timp una lipseste sau perioada nu e buna, butonul `Apply` sta stins si
 * motivul scrie sub casute. Nimic nu se taie in tacere: o perioada de o suta
 * douazeci de zile nu devine una de nouazeci, ci e refuzata cu motivul pe
 * ecran - la fel si in server, cu acelasi text.
 */
export function BoardWindow({
  windows,
  days,
  range,
  said,
  maxDays,
  hrefFor,
}: {
  /** Ferestrele fixe, din server: `{ '1': 'Today', '7': 'Last 7 days', ... }`. */
  windows: Record<string, string>
  /** Fereastra pe zile de acum. */
  days: number
  /** Perioada asezata, cand e una. */
  range: BoardRange | null
  /** De ce n-a fost primita perioada ceruta - vine din server. */
  said: string
  maxDays: number
  /** Adresa tabloului pentru o fereastra: zile de-a gata, sau perioada. */
  hrefFor: (days: number, range: BoardRange | null) => string
}) {
  const [open, setOpen] = useState(false)
  const [from, setFrom] = useState(range?.from ?? '')
  const [to, setTo] = useState(range?.to ?? '')
  const box = useRef<HTMLDivElement>(null)

  /* Panoul se deschide pe fereastra de acum: cu o perioada asezata, pe ea; fara
     una, pe fereastra pe zile, ca omul sa aiba de unde porni in loc de doua
     casute goale. */
  const opensOn = (): BoardRange => {
    if (range) return range
    const end = new Date()
    const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - (days - 1))
    return { from: today(start), to: today(end) }
  }

  function show() {
    const on = opensOn()
    setFrom(range?.from ?? on.from)
    setTo(range?.to ?? on.to)
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const shut = (ev: MouseEvent) => {
      if (box.current && !box.current.contains(ev.target as Node)) setOpen(false)
    }
    const key = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', shut)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', shut)
      document.removeEventListener('keydown', key)
    }
  }, [open])

  /* Motivul de sub casute: intai ce spune ecranul despre ce se scrie acum, iar
     cand n-are nimic de spus, ce a spus serverul despre ce s-a cerut. */
  const wrong = from !== '' && to !== '' ? checkRange(from, to, maxDays) : ''
  const ready = from !== '' && to !== '' && wrong === ''
  const span = ready ? daysBetween(from, to) : 0

  return (
    <div className="odb-windows">
      {Object.entries(windows).map(([n, label]) => (
        <a
          className={`odb-window${!range && Number(n) === days ? ' is-on' : ''}`}
          title={Number(n) === 1 ? 'The last 24 hours' : `The last ${n} days`}
          href={hrefFor(Number(n), null)}
          key={n}
        >
          {label}
        </a>
      ))}
      <div ref={box} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          type="button"
          className={`odb-window${range ? ' is-on' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          title="Pick a start and an end date yourself"
          onClick={() => (open ? setOpen(false) : show())}
        >
          <i className="fas fa-calendar-alt" aria-hidden="true"></i>{' '}
          {range ? rangeLabel(range) : 'Custom'}
        </button>
        {open && (
          <div
            className="odb-window-panel"
            role="dialog"
            aria-label="Custom period"
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              zIndex: 30,
              marginTop: 4,
              padding: 12,
              minWidth: 320,
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: 4,
              boxShadow: '0 6px 18px rgba(0,0,0,.18)',
              textAlign: 'left',
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <DatePicker value={from} name="from" id="odbRangeFrom" typed onChange={setFrom} />
              <span>-</span>
              <DatePicker value={to} name="to" id="odbRangeTo" typed onChange={setTo} />
            </div>
            {/* Intai ce e in neregula cu ce se scrie ACUM, apoi ce a spus
                serverul despre ce s-a cerut in adresa, si abia la urma, cand
                n-are nimeni nimic de reclamat, cat tine perioada. */}
            <p style={{ margin: '8px 0 0', minHeight: 18, fontSize: 12, color: wrong || said ? '#a94442' : '#777' }}>
              {wrong || said || (ready ? `${span} days` : '')}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              {range && (
                <a className="odb-btn" href={hrefFor(days, null)}>
                  Clear
                </a>
              )}
              <button type="button" className="odb-btn" onClick={() => setOpen(false)}>
                Cancel
              </button>
              {/* Cand perioada e buna, Apply e o LEGATURA: aceeasi adresa pe
                  care ar scrie-o omul, deci tabloul cu perioada pe el se
                  copiaza cu dreapta si se trimite mai departe.

                  Cand nu e, e un buton stins - nu o legatura fara `href`, care
                  nu mai e legatura nici pentru cititorul de ecran si nu se mai
                  poate nici atinge cu Tab: omul de la tastatura ar fi ramas cu
                  un cuvant gri fara nicio explicatie. */}
              {ready ? (
                <a className="odb-btn odb-btn-main" href={hrefFor(span, { from, to })}>
                  Apply
                </a>
              ) : (
                <button type="button" className="odb-btn odb-btn-main" disabled>
                  Apply
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
